import { useMemo } from 'react';
import {
  Campaign,
  Status_Enum as CampaignStatus,
  CampaignType_Enum as CampaignType,
  Usage,
} from '@weave/schema-gen-ts/dist/schemas/messaging/bulk/v2';
import dayjs from 'dayjs';
import { useGetUsage, flattenLocationUsages } from '@frontend/api-bulk-messaging';
import { useAppScopeStore } from '@frontend/scope';
import { LIMITED_EDIT_CAMPAIGN_STATUSES } from '../constants';

type Props = {
  messageCount?: number;
  campaign?: Campaign;
  campaignType: CampaignType;
  locationIds: string[];
  selectedDate: string;
};

export const useMonthUsage = ({ messageCount, campaign, campaignType, locationIds, selectedDate }: Props) => {
  const { selectedOrgId: orgId } = useAppScopeStore();

  const { displayedMonth, displayedYear, isSamePeriod } = useMemo(
    () => determineDisplayDate(selectedDate, campaign?.startSendAt),
    [selectedDate, campaign?.startSendAt]
  );

  const { data: locationUsages, isFetching } = useGetUsage(
    {
      type: campaignType,
      orgId,
      locationIds,
      year: displayedYear,
    },
    displayedMonth
  );

  const usage = useMemo(() => {
    if (!locationUsages) {
      return { month: displayedMonth, allotment: 0, error: 0, scheduled: 0, sent: 0 };
    }

    return flattenLocationUsages(locationUsages).reduce<Usage>(
      (acc, locationUsage) => {
        if (locationUsage.month !== acc.month) return acc;
        return {
          month: acc.month,
          allotment: acc.allotment + (locationUsage.allotment ?? 0),
          error: acc.error + (locationUsage.error ?? 0),
          scheduled: acc.scheduled + (locationUsage.scheduled ?? 0),
          sent: acc.sent + (locationUsage.sent ?? 0),
        };
      },
      { month: displayedMonth, allotment: 0, error: 0, scheduled: 0, sent: 0 }
    );
  }, [locationUsages, displayedMonth]);

  // Extract usage data for the displayed month
  const { monthAllotment, monthUsed, monthPending } = useMemo(
    () => ({
      monthAllotment: usage.allotment,
      monthUsed: usage.sent + usage.error,
      monthPending: usage.scheduled,
    }),
    [usage]
  );

  // Extract usage data from the campaign
  const { campaignPlanned, campaignUsed, campaignPending } = useMemo(() => {
    const planned = messageCount ?? 0;
    const sent = campaign?.messageCounts?.delivered ?? 0;
    const failed = campaign?.messageCounts?.failed ?? 0;
    return {
      campaignPlanned: planned,
      campaignUsed: sent + failed,
      campaignPending: planned - sent - failed,
    };
  }, [messageCount, campaign?.messageCounts]);

  // Determine if the campaign should deduct from the displayed month's usage
  const shouldDeductFromMonth = useMemo(() => {
    const campaignStatus = campaign?.currentStatus || CampaignStatus.UNSPECIFIED;
    return LIMITED_EDIT_CAMPAIGN_STATUSES.includes(campaignStatus) && isSamePeriod;
  }, [campaign?.currentStatus, isSamePeriod]);

  // Calculate final allotments
  const { usedAllotment, pendingAllotment, remainingAllotment } = useMemo(() => {
    const usedAllotment = shouldDeductFromMonth
      ? ensureNonNegative(monthUsed - campaignUsed)
      : ensureNonNegative(monthUsed);

    const pendingAllotment = shouldDeductFromMonth
      ? ensureNonNegative(monthPending - campaignPending)
      : ensureNonNegative(monthPending);

    const totalAllotment = usedAllotment + pendingAllotment;
    const remainingAllotment = monthAllotment - totalAllotment - campaignPlanned;

    return { usedAllotment, pendingAllotment, remainingAllotment };
  }, [shouldDeductFromMonth, monthUsed, campaignUsed, monthPending, campaignPending, monthAllotment, campaignPlanned]);

  return {
    month: displayedMonth,
    monthMetrics: {
      allotment: usage.allotment,
      used: usage.sent + usage.scheduled,
      pending: usage.scheduled,
      remaining: usage.allotment - (usage.sent + usage.scheduled + usage.error),
    },
    campaignMetrics: {
      allotment: campaignPlanned,
      used: usedAllotment,
      pending: pendingAllotment,
      remaining: remainingAllotment,
    },
    isFetching,
  };
};

const determineDisplayDate = (selectedDate: string, campaignStartDate?: string) => {
  // selectedMonth/selectedYear: From editor selection
  const selectedMonth = dayjs(selectedDate).month() + 1;
  const selectedYear = dayjs(selectedDate).year();

  // campaignMonth/campaignYear: From scheduled campaign (edit/duplicate)
  const campaignMonth = campaignStartDate ? dayjs(campaignStartDate).month() + 1 : undefined;
  const campaignYear = campaignStartDate ? dayjs(campaignStartDate).year() : undefined;

  // currentMonth/currentYear: Fallback (create)
  const currentMonth = dayjs().month() + 1;
  const currentYear = dayjs().year();

  const displayedMonth = selectedMonth || campaignMonth || currentMonth;
  const displayedYear = selectedYear || campaignYear || currentYear;

  const isSamePeriod = campaignMonth === selectedMonth && campaignYear === selectedYear;

  return {
    displayedMonth,
    displayedYear,
    isSamePeriod,
  };
};

const ensureNonNegative = (value: number) => (value < 0 ? 0 : value);
